<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useAuth } from '@/composables/useAuth'
import { hasAdminRole } from '@/types/auth'
import { useUnifiedPermission } from '@/composables/useUnifiedPermission'
import { PERMISSION_CODES } from '@/utils/permissions'
import {
  ArrowDown, Bell, Calendar, Collection, DataAnalysis, Document,
  DocumentChecked, DocumentCopy, Files, HomeFilled, List, Lock,
  Memo, Menu, OfficeBuilding, Promotion, SetUp, Setting,
  Ticket, TrendCharts, User, UserFilled, FolderOpened, Edit, Search,
} from '@element-plus/icons-vue'
import type { Component } from 'vue'
import UserAvatar from '@/components/common/UserAvatar.vue'
import { useNotificationStore } from '@/stores/notifications'

const route = useRoute()
const { state } = useAuth()
const notifyStore = useNotificationStore()

const userName = computed(() => state.currentUser?.displayName ?? '')
const userAvatarUrl = computed(() => state.currentUser?.avatarUrl || '')
const orgName = computed(() => state.currentUser?.organizationName || '')
const isAdmin = computed(() => hasAdminRole(state.currentUser?.role))
const isManager = computed(() => state.currentUser?.role === 'manager' && !isPersonalSpace.value)
const isPersonalSpace = computed(() => state.currentUser?.organizationType === 'personal')
const { hasPermission } = useUnifiedPermission()
const canAnalyze = computed(
  () =>
    hasPermission(PERMISSION_CODES.WORKSPACE_ANALYSIS) ||
    hasPermission(PERMISSION_CODES.WORKSPACE_ANALYSIS_VIEW),
)
const canAccessReport = computed(() => hasPermission(PERMISSION_CODES.WORKSPACE_REPORT))

const iconMap: Record<string, Component> = {
  ArrowDown, Bell, Calendar, Collection, DataAnalysis, Document,
  DocumentChecked, DocumentCopy, Files, HomeFilled, List, Lock,
  Memo, Menu, OfficeBuilding, Promotion, SetUp, Setting,
  Ticket, TrendCharts, User, UserFilled, FolderOpened, Edit, Search,
}

interface MenuItem {
  to: string
  label: string
  icon: string
  badge?: number
  /** 访问所需的权限码；缺省表示不做权限过滤 */
  permission?: string
}

interface MenuGroup {
  label: string
  icon: string
  children: MenuItem[]
}

const standardMenus = computed<MenuItem[]>(() => [
  { to: '/dashboard', label: '工作台', icon: 'HomeFilled' },
  { to: '/ledgers', label: '我的台账', icon: 'Document' },
  { to: '/records', label: '工作记录', icon: 'Memo' },
  // 修复任务审查#2：与路由 meta 同码门控——SYS_ADMIN 不持 workspace:task:view，
  // 旧写法菜单照常显示、点进去是被路由守卫拦回的空白页
  ...(hasPermission(PERMISSION_CODES.TASK_VIEW) ? [{ to: '/tasks', label: '工作任务', icon: 'List' as const }] : []),
  // 修复文档审查#19：此前菜单项零门控，未持 doc:center 的自定义角色照常看到入口、
  // 点进去被路由守卫/后端 403；同列表的任务与迎检都按码门控，此处补齐
  ...(hasPermission(PERMISSION_CODES.DOC_CENTER) ? [{ to: '/document', label: '文档中心', icon: 'FolderOpened' as const }] : []),
  ...(hasPermission(PERMISSION_CODES.DOC_KB_CHAT) ? [{ to: '/document/knowledge', label: '知识库', icon: 'Collection' as const }] : []),
  // 修复迎检审查#4：按 view 码门控入口（此前零权限码，与知识库/宣传报道口径不一）
  ...(hasPermission(PERMISSION_CODES.INSPECTION_VIEW) ? [{ to: '/inspections', label: '迎检日历', icon: 'Calendar' as const }] : []),
  // 宣传报道：leader/manager（组织）与 super_admin 可见；staff 无权限码，避免点进 403 报错页
  ...(canAccessReport.value ? [{ to: '/reports', label: '宣传报道', icon: 'Promotion' as const }] : []),
  ...(canAnalyze.value ? [{ to: '/analysis', label: '智能分析', icon: 'DataAnalysis' as const }] : []),
  { to: '/messages', label: '消息通知', icon: 'Bell', badge: notifyStore.unreadTotal || undefined },
  { to: '/settings', label: '账号设置', icon: 'Setting' },
])

const adminMenuGroups = computed<MenuGroup[]>(() => {
  const groups: MenuGroup[] = [
    {
      label: '系统概览',
      icon: 'HomeFilled',
      children: [
        // 修复系统概览审查#3：与路由 meta.permission 同口径门控。此前该子项无 permission，
        // 未持 admin:overview 的账号恒看得见菜单、点进去被路由守卫拦回，等于死按钮。
        { to: '/admin/overview', label: '系统概览', icon: 'HomeFilled', permission: PERMISSION_CODES.ADMIN_OVERVIEW },
        // 智能分析：仅拥有 workspace:analysis 权限的用户可见（SUPER_ADMIN / ORG_ADMIN / ORG_LEADER）
        ...(canAnalyze.value
          ? [{ to: '/analysis', label: '智能分析', icon: 'DataAnalysis' as const }]
          : []),
      ],
    },
    {
      // 文档/系统配置/首页内容/单位/角色/用户六类变更留痕合并为一页，
      // 原先分散在 5 个菜单组里的日志入口（含角色权限组的悬空码 ADMIN_ROLE_LOG）随之删除。
      label: '审计日志',
      icon: 'List',
      children: [
        { to: '/admin/audit-logs', label: '审计日志', icon: 'List', permission: PERMISSION_CODES.ADMIN_AUDIT_VIEW },
      ],
    },
    {
      // 修复 P3#26：合并套餐管理/统计/订单/订阅 4 个页面为「计费与套餐」一级菜单。
      label: '计费与套餐',
      icon: 'Ticket',
      children: [
        { to: '/admin/plans', label: '套餐管理', icon: 'Ticket' },
        { to: '/admin/plan-stats', label: '套餐统计', icon: 'TrendCharts' },
        { to: '/admin/orders', label: '订单管理', icon: 'DocumentCopy' },
        { to: '/admin/subscriptions', label: '订阅管理', icon: 'Collection' },
      ],
    },
    {
      label: '文档中心',
      icon: 'FolderOpened',
      children: [
        { to: '/document', label: '文档中心', icon: 'FolderOpened', permission: PERMISSION_CODES.DOC_CENTER },
        { to: '/document/knowledge', label: '知识库问答', icon: 'Collection', permission: PERMISSION_CODES.DOC_KB_CHAT },
        { to: '/document/sync-status', label: '同步状态看板', icon: 'DocumentChecked', permission: PERMISSION_CODES.DOC_KB_SYNC },
        { to: '/document/stats', label: '统计报表', icon: 'TrendCharts', permission: PERMISSION_CODES.DOC_STATS_VIEW },
        // 水印配置仅超管/系统管理员可见（与路由 meta、后端 /api/admin/watermark-configs 同口径）
        { to: '/document/watermark-config', label: '水印配置', icon: 'Lock', permission: PERMISSION_CODES.DOC_WATERMARK_CONFIG },
      ],
    },
    {
      label: '单位管理',
      icon: 'OfficeBuilding',
      children: [
        // 修复单位审查#3：菜单门控与路由 meta / 后端细码同源
        { to: '/admin/organizations', label: '单位管理', icon: 'OfficeBuilding', permission: PERMISSION_CODES.ADMIN_ORG_VIEW },
        { to: '/admin/org-applications', label: '入驻审批', icon: 'DocumentChecked', permission: PERMISSION_CODES.ADMIN_ORG_APPROVE },
      ],
    },
    {
      label: '用户管理',
      icon: 'User',
      children: [
        { to: '/admin/users', label: '用户管理', icon: 'User' },
      ],
    },
    // 宣传报道管理：仅拥有 workspace:report 权限的用户可见
    ...(canAccessReport.value
      ? [{
          label: '宣传报道',
          icon: 'Promotion' as const,
          children: [
            { to: '/admin/reports', label: '宣传报道管理', icon: 'Promotion' as const },
          ],
        }]
      : []),
    {
      label: '模板管理',
      icon: 'Document',
      children: [
        // C6 配套：按模板权限码门控（后端 RoleInitService 目录已登记，仅 SYS_ADMIN 预置授予）
        { to: '/admin/templates', label: '模板管理', icon: 'Document', permission: PERMISSION_CODES.ADMIN_TEMPLATE_VIEW },
        { to: '/admin/presets', label: '组件预制', icon: 'SetUp', permission: PERMISSION_CODES.ADMIN_TEMPLATE_VIEW },
      ],
    },
    {
      label: '首页内容管理',
      icon: 'FolderOpened',
      children: [
        // 按首页内容管理权限码门控(后端 RoleInitService 目录已登记,仅 SYS_ADMIN 预置授予);
        // 此前子项无 permission,无该权限码的普通 admin 会看到菜单但点进去 403
        { to: '/admin/landing-page/sections', label: '区块配置', icon: 'List', permission: PERMISSION_CODES.ADMIN_LANDING_PAGE_MANAGE },
        { to: '/admin/landing-page/hero', label: 'Hero 区', icon: 'Promotion', permission: PERMISSION_CODES.ADMIN_LANDING_PAGE_MANAGE },
        { to: '/admin/landing-page/features', label: '功能卡片', icon: 'Collection', permission: PERMISSION_CODES.ADMIN_LANDING_PAGE_MANAGE },
        { to: '/admin/landing-page/stats', label: '数据佐证', icon: 'TrendCharts', permission: PERMISSION_CODES.ADMIN_LANDING_PAGE_MANAGE },
        { to: '/admin/landing-page/testimonials', label: '用户证言', icon: 'Memo', permission: PERMISSION_CODES.ADMIN_LANDING_PAGE_MANAGE },
        { to: '/admin/landing-page/docs-news', label: '文档资讯', icon: 'DocumentCopy', permission: PERMISSION_CODES.ADMIN_LANDING_PAGE_MANAGE },
        { to: '/admin/landing-page/process', label: '流程可视化', icon: 'SetUp', permission: PERMISSION_CODES.ADMIN_LANDING_PAGE_MANAGE },
        { to: '/admin/landing-page/scenarios', label: '场景演示', icon: 'DataAnalysis', permission: PERMISSION_CODES.ADMIN_LANDING_PAGE_MANAGE },
        { to: '/admin/landing-page/kanban', label: '看板预览', icon: 'Calendar', permission: PERMISSION_CODES.ADMIN_LANDING_PAGE_MANAGE },
        { to: '/admin/landing-page/cta-footer', label: 'CTA/Footer', icon: 'Edit', permission: PERMISSION_CODES.ADMIN_LANDING_PAGE_MANAGE },
        { to: '/admin/landing-page/seo', label: 'SEO 元数据', icon: 'Search', permission: PERMISSION_CODES.ADMIN_LANDING_PAGE_MANAGE },
      ],
    },
    // 修复角色权限三审#1：整组由「按超管身份硬门控」改为按 admin:role:view 权限码门控（子项已挂 permission）。
    // 后端 RoleAdminController/PermissionAdminController 已从 hasRole('SUPER_ADMIN') 收口到 admin:role:* 码，
    // SYS_ADMIN 预设持有 admin:role:view 即可见，普通 admin 无此码则子项被过滤、整组隐藏（见 visibleMenuGroups）。
    {
      label: '角色权限管理',
      icon: 'Lock',
      children: [
        { to: '/admin/roles', label: '角色列表', icon: 'Lock' as const, permission: PERMISSION_CODES.ADMIN_ROLE_VIEW },
        { to: '/admin/role-permission/templates', label: '权限模板', icon: 'Memo' as const, permission: PERMISSION_CODES.ADMIN_ROLE_VIEW },
      ],
    },
    {
      label: '系统消息',
      icon: 'Bell',
      // 修复消息审查#8：四子项补 permission: ADMIN_MESSAGE（与路由 meta 同码），
      // 此前无 permission，未持 admin:message 的自定义角色也恒见整组菜单、点进去 403。
      children: [
        { to: '/admin/system-messages/announcements', label: '系统公告与通知', icon: 'Bell', permission: PERMISSION_CODES.ADMIN_MESSAGE },
        { to: '/admin/system-messages/feedbacks', label: '用户反馈管理', icon: 'DocumentCopy', permission: PERMISSION_CODES.ADMIN_MESSAGE },
        { to: '/admin/system-messages/templates', label: '消息模板管理', icon: 'Memo', permission: PERMISSION_CODES.ADMIN_MESSAGE },
        { to: '/admin/system-messages/send-logs', label: '发送日志', icon: 'List', permission: PERMISSION_CODES.ADMIN_MESSAGE },
      ],
    },
    // 内容安全：敏感词库管理（后端 hasRole('ADMIN')，/admin 总闸已按角色把关，不叠权限码）
    {
      label: '内容安全',
      icon: 'Lock',
      children: [{ to: '/admin/sensitive-words', label: '敏感词库', icon: 'Lock' as const }],
    },
    {
      label: '系统配置',
      icon: 'Setting',
      // 修复配置审查#12：子项按 ADMIN_CONFIG_READ 门控（与路由 meta 同码），
      // 此前无 permission，普通 admin 之外的自定义角色即便无读取码也会看到全部菜单，
      // 点进去 403。发票配置项随该整域下线删除。
      children: [
        { to: '/admin/system-config/basic', label: '基础设置', icon: 'Setting', permission: PERMISSION_CODES.ADMIN_CONFIG_READ },
        { to: '/admin/system-config/ai-models', label: 'AI 模型配置', icon: 'SetUp', permission: PERMISSION_CODES.ADMIN_CONFIG_READ },
        { to: '/admin/system-config/payment', label: '支付配置', icon: 'Ticket', permission: PERMISSION_CODES.ADMIN_CONFIG_READ },
        { to: '/admin/system-config/security', label: '安全策略', icon: 'Lock', permission: PERMISSION_CODES.ADMIN_CONFIG_READ },
        { to: '/admin/system-config/notification', label: '通知配置', icon: 'Bell', permission: PERMISSION_CODES.ADMIN_CONFIG_READ },
        { to: '/admin/system-config/legal', label: '法律信息', icon: 'DocumentChecked', permission: PERMISSION_CODES.ADMIN_CONFIG_READ },
        { to: '/admin/system-config/archive-rules', label: '归档规则', icon: 'Files', permission: PERMISSION_CODES.ADMIN_CONFIG_READ },
        // 账号安全不做 permission 门控（与路由 meta 同口径）：改的是当前登录者自己的密码，
        // 后端只要求 authenticated，按配置码门控会把无 admin:config:read 的管理员挡在门外
        { to: '/admin/system-config/account', label: '账号安全', icon: 'UserFilled' },
      ],
    },
  ]
  return groups
})

const managerExtraMenus = computed<MenuItem[]>(() => [
  { to: '/workspace/members', label: '人员管理', icon: 'User' },
])

// 按权限过滤后的管理员菜单组：子项按 permission 过滤（super_admin 在 hasPermission 内部已短路放行），
// 子项全部无权时隐藏整组（与 canAccessReport 的组级过滤模式一致）
const visibleMenuGroups = computed<MenuGroup[]>(() =>
  adminMenuGroups.value
    .map((group) => ({
      ...group,
      children: group.children.filter(
        (child) => !child.permission || hasPermission(child.permission),
      ),
    }))
    .filter((group) => group.children.length > 0),
)

// 展开状态：记录每个一级菜单是否展开
const expandedKeys = ref<Set<string>>(new Set())

function toggleGroup(label: string) {
  if (expandedKeys.value.has(label)) {
    expandedKeys.value.delete(label)
  } else {
    expandedKeys.value.add(label)
  }
}

function isGroupActive(group: MenuGroup): boolean {
  return group.children.some(child => isActive(child.to))
}

// 监听路由变化，自动展开当前激活的菜单组
watch(
  () => route.path,
  () => {
    if (!isAdmin.value) return
    visibleMenuGroups.value.forEach(group => {
      if (isGroupActive(group)) {
        expandedKeys.value.add(group.label)
      }
    })
  },
  { immediate: true }
)

const menus = computed(() => {
  if (isAdmin.value) return null
  // Manager: 普通用户菜单 + 人员管理（插入到"账号设置"上方）
  if (isManager.value) {
    const base = standardMenus.value
    return [...base.slice(0, -1), ...managerExtraMenus.value, base[base.length - 1]]
  }
  return standardMenus.value
})
const menuGroups = computed(() => (isAdmin.value ? visibleMenuGroups.value : null))

function isActive(to: string): boolean {
  if (to === '/dashboard' || to === '/admin' || to === '/document') {
    return route.path === to
  }
  if (route.path === to) {
    return true
  }
  // 仅当 to 是 route.path 的完整路径前缀时才让子级路由激活父级菜单
  if (route.path.startsWith(to + '/')) {
    const remaining = route.path.slice(to.length + 1)
    return remaining.length > 0 && !remaining.startsWith('/')
  }
  return false
}
</script>

<template>
  <aside class="sidebar hidden w-60 md:flex md:flex-col h-full">
    <!-- 用户信息 - 固定在顶部 -->
    <div class="shrink-0 px-4 pt-5 pb-3">
      <div class="user-card flex items-center gap-3 rounded-xl p-3.5">
        <div class="flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] text-white shadow-sm">
          <UserAvatar :src="userAvatarUrl">
            <ElIcon size="18">
              <component :is="isAdmin ? iconMap.UserFilled : iconMap.User" />
            </ElIcon>
          </UserAvatar>
        </div>
        <div class="min-w-0">
          <div class="truncate text-sm font-semibold text-[var(--color-title)]">{{ userName }}</div>
          <div v-if="isPersonalSpace" class="truncate text-xs font-medium text-[var(--color-warning)]">个人体验空间</div>
          <div v-else-if="orgName" class="truncate text-xs text-[var(--color-caption)]">{{ orgName }}</div>
          <div v-else-if="isAdmin" class="truncate text-xs text-[var(--color-caption)]">系统管理员</div>
        </div>
      </div>
    </div>

    <!-- 分割线 -->
    <div class="mx-4 mb-2 h-px bg-[var(--color-divider)]"></div>

    <!-- 菜单区域 - 独立滚动 -->
    <div class="flex-1 overflow-y-auto px-3 pb-4">
      <!-- 普通用户：平级菜单 -->
      <nav v-if="menus" class="space-y-0.5">
        <RouterLink
          v-for="(item, index) in menus"
          :key="item.to"
          :to="item.to"
          class="nav-item group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-all duration-200"
          :class="
            isActive(item.to)
              ? 'nav-item-active font-semibold text-[var(--color-primary)] bg-[var(--color-primary-light)]'
              : 'text-[var(--color-body)] hover:bg-[var(--color-card-bg)]'
          "
          :style="{ animationDelay: index * 30 + 'ms' }"
        >
          <div
            class="nav-item-icon flex h-8 w-8 items-center justify-center rounded-lg transition-all duration-200"
            :class="isActive(item.to) ? 'bg-[var(--color-primary)] text-white shadow-sm' : 'bg-[var(--color-card-bg)] text-[var(--color-caption)] group-hover:text-[var(--color-primary)]'"
          >
            <ElIcon size="16">
              <component :is="iconMap[item.icon]" />
            </ElIcon>
          </div>
          <span class="flex-1">{{ item.label }}</span>
          <ElBadge v-if="item.badge" :value="item.badge" class="ml-auto" />
        </RouterLink>
      </nav>

      <!-- 管理员：两级菜单 -->
      <nav v-if="menuGroups" class="space-y-0.5">
        <div v-for="group in menuGroups" :key="group.label" class="menu-group">
          <!-- 一级菜单 -->
          <button
            class="nav-item group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-all duration-200"
            :class="
              isGroupActive(group)
                ? 'font-semibold text-[var(--color-primary)] bg-[var(--color-primary-light)]'
                : 'text-[var(--color-body)] hover:bg-[var(--color-card-bg)]'
            "
            @click="toggleGroup(group.label)"
          >
            <div
              class="nav-item-icon flex h-8 w-8 items-center justify-center rounded-lg transition-all duration-200"
              :class="isGroupActive(group) ? 'bg-[var(--color-primary)] text-white shadow-sm' : 'bg-[var(--color-card-bg)] text-[var(--color-caption)] group-hover:text-[var(--color-primary)]'"
            >
              <ElIcon size="16">
                <component :is="iconMap[group.icon]" />
              </ElIcon>
            </div>
            <span class="flex-1 text-left">{{ group.label }}</span>
            <ElIcon
              class="text-[var(--color-caption)] transition-transform duration-300"
              :class="expandedKeys.has(group.label) ? 'rotate-180' : ''"
            >
              <component :is="iconMap.ArrowDown" />
            </ElIcon>
          </button>

          <!-- 二级菜单 -->
          <Transition name="expand">
            <div
              v-show="expandedKeys.has(group.label)"
              class="ml-4 mt-0.5 space-y-0.5 overflow-hidden border-l-2 border-[var(--color-primary-light)] pl-3"
            >
              <RouterLink
                v-for="child in group.children"
                :key="child.to"
                :to="child.to"
                class="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-all duration-200"
                :class="
                  isActive(child.to)
                    ? 'font-medium text-[var(--color-primary)] bg-[var(--color-primary-light)]'
                    : 'text-[var(--color-caption)] hover:text-[var(--color-body)] hover:bg-[var(--color-card-bg)]'
                "
              >
                <ElIcon size="14">
                  <component :is="iconMap[child.icon]" />
                </ElIcon>
                {{ child.label }}
                <ElBadge v-if="child.badge" :value="child.badge" class="ml-auto" />
              </RouterLink>
            </div>
          </Transition>
        </div>
      </nav>
    </div>

    <!-- 底部版本信息 -->
    <div class="shrink-0 border-t border-[var(--color-divider)] px-5 py-3">
      <div class="text-xs text-[var(--color-caption)]">智台账 v1.0</div>
    </div>
  </aside>
</template>

<style scoped>
.sidebar {
  background: var(--color-card);
  border-right: 1px solid var(--color-divider);
}

.user-card {
  background: var(--gradient-primary-soft);
  border: 1px solid rgba(59, 91, 219, 0.06);
}

.nav-item {
  position: relative;
}

.nav-item-active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 60%;
  border-radius: 0 3px 3px 0;
  background: var(--gradient-primary);
}

/* 展开过渡 */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  max-height: 600px;
  opacity: 1;
}
.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
}

html.dark .sidebar {
  background: var(--color-card);
}
</style>
